import type { ApprovalMode, EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address, PublicClient } from "viem"
import type { Config } from "wagmi"
import { ClFarmStore } from "@/domains/cl-farm"
import { approveOwnerSpenderAllowance, mintFertilizerClFarmLiquidity } from "@/domains/on-chain"
import { erc20Abi } from "viem"

type MintFertilizerClFarmLiquidityInteraction = {
  amountToken0BI: bigint
  amountToken1BI: bigint
  approvalMode: ApprovalMode
  chainId: number
  clFarm: ClFarmStore
  config: Config
  connectedAddress: Address
  emitter: EventEmitter<EmitEvent>
  publicClient: PublicClient
  onSuccess: () => void
}

export const mintFertilizerClFarmLiquidityInteraction = async ({
  amountToken0BI,
  amountToken1BI,
  approvalMode,
  chainId,
  clFarm,
  config,
  connectedAddress,
  emitter,
  publicClient,
  onSuccess,
}: MintFertilizerClFarmLiquidityInteraction) => {
  const vaultAddress = clFarm?.vaultAddress?.toViemAddress()
  if (!vaultAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm vault address is invalid. Please try again.",
      variant: "error",
    })
  }
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }
  // The user must enter at least one of the input tokens amounts
  if (amountToken0BI <= 0n && amountToken1BI <= 0n) {
    emitter.emit("error-insufficient-inputs", {
      title: "Insufficient inputs",
      description: "You must enter at least one of the input token amounts",
      variant: "error",
    })
    return
  }

  // Check if the user has enough token 0 balance in their wallet
  if (clFarm.token0.balance > 0n && clFarm.token0.balance < amountToken0BI) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${clFarm.token0.symbol} in your wallet. You are trying to mint ${clFarm.token0.formatUnits(amountToken0BI - clFarm.token0.balance)} more than available.`,
      variant: "error",
    })
    return
  }

  // Check if the user has enough token 1 balance in their wallet
  if (clFarm.token1.balance > 0n && clFarm.token0.balance < amountToken1BI) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${clFarm.token0.symbol} in your wallet. You are trying to mint ${clFarm.token0.formatUnits(amountToken1BI - clFarm.token1.balance)} more than available.`,
      variant: "error",
    })
    return
  }

  //#region STEP TWO - Does the user need to provide an allowance?
  const mintTokenAllowance0 = await publicClient.readContract({
    address: clFarm.token0.address.toViemAddress(),
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, vaultAddress],
  })

  // If they have entered an amount for the first token, and they don't have enough allowance, we need to seek approval
  if (amountToken0BI > 0n && mintTokenAllowance0 < amountToken0BI) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${clFarm.token0.formatUnits(amountToken0BI)} ${clFarm.token0.symbol}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: clFarm.token0.address.toViemAddress(),
      spenderAddress: vaultAddress,
      spenderAmount: amountToken0BI,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You may now proceed to mint",
        variant: "success",
      })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error ?? "unknown error",
        variant: "error",
      })
      return
    }
  }

  const mintTokenAllowance1 = await publicClient.readContract({
    address: clFarm.token1.address.toViemAddress(),
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, vaultAddress],
  })

  // If they have entered an amount for the second token, and they don't have enough allowance, we need to seek approval
  if (amountToken1BI > 0n && mintTokenAllowance1 < amountToken1BI) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${clFarm.token1.formatUnits(amountToken1BI)} ${clFarm.token1.symbol}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: clFarm.token1.address.toViemAddress(),
      spenderAddress: vaultAddress,
      spenderAmount: amountToken1BI,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You may now proceed to mint",
        variant: "success",
      })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error ?? "unknown error",
        variant: "error",
      })
      return
    }
  }

  // STEP THREE: Withdraw the CL Farm Liquidity from the Farm
  const slippageBI = (BigInt(amountToken0BI + amountToken1BI) * BigInt(Number(clFarm.slipBps ?? 0))) / BigInt(1e4)

  const mintClFarmLiquidityResult = await mintFertilizerClFarmLiquidity({
    account: connectedAddress,
    chainId,
    config,
    vaultAddress,
    mintToken0AmountBI: amountToken0BI,
    mintToken1AmountBI: amountToken1BI,
    slippageBI,
  })

  if (mintClFarmLiquidityResult.success) {
    onSuccess?.()

    const descriptionParts = []
    if (amountToken0BI > 0n) {
      descriptionParts.push(`${clFarm.token0.formatUnits(amountToken0BI)} ${clFarm.token0.symbol}`)
    }

    if (amountToken1BI > 0n) {
      descriptionParts.push(`${clFarm.token1.formatUnits(amountToken1BI)} ${clFarm.token1.symbol}`)
    }

    emitter.emit("success-mint-cl-farm-liquidity", {
      title: `Mint ${clFarm.displayName}`,
      description: `Minted ${clFarm.displayName} tokens with ${descriptionParts.join(" and ")}`,
      variant: "success",
    })
  } else {
    emitter.emit("error-mint-cl-farm-liquidity", {
      title: `Unable to mint ${clFarm.token0.formatUnits(amountToken0BI)} ${clFarm.displayName}`,
      description: mintClFarmLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }

  return mintClFarmLiquidityResult
}
